// ===== 备份与恢复服务 =====
// 需求 9：崩溃后可通过备份文件快速恢复整个工作台
// 多通道备份体系：
//   1) 本地一键导出 / 导入备份包（.json）—— 不依赖任何第三方，100% 可用
//   2) 阿里云盘自动备份（见 aliyunpan.ts）—— 绑定账号后自动上传，换设备/系统崩溃都能取回
//   3) 飞牛 NAS 智能同步（见 webdav.ts）—— WebDAV 直连，支持多台 NAS，开机时自动补推最新备份
//   4) Supabase Storage（浏览器端 REST 直传，零依赖）—— 绑定项目 URL + anon key + bucket 后上传
//   5) 七牛云 Kodo（Supabase Edge Function 签名 + 浏览器直传）—— AK/SK 仅服务端持有，前端拿临时 token 直传
// 备份包结构：{ app, type, formatVersion, dataVersion, exportedAt, stats,
//              data(IndexedDB 6类), businessData(localStorage 业务数据), settings }
// v2：新增 仓库/回收站(IndexedDB) + 进货明细/出库单/盘点/零售全部表格(localStorage 业务数据)

import { KEYS, DATA_VERSION, dbGet, dbSet } from '@/lib/db';
import {
  setMockCustomers,
  setMockSuppliers,
  setMockProducts,
  setMockSales,
} from '@/data/mockData';
import { webDavUpload, type WebDavConfig } from '@/lib/webdav';
import { qiniuUpload, qiniuList, qiniuDownloadText, type QiniuConfig, type QiniuFile } from '@/lib/qiniu';

export interface BackupStats {
  customers: number;
  suppliers: number;
  products: number;
  salesOrders: number;
  warehouses: number;
  trash: number;
  businessData: number;
  settings: number;
}

export interface BackupFile {
  app: 'changfa-workbench';
  type: 'changfa-backup';
  formatVersion: 2;
  dataVersion: string;
  exportedAt: string;
  stats: BackupStats;
  data: {
    customers: unknown[];
    suppliers: unknown[];
    products: unknown[];
    salesOrders: unknown[];
    warehouses: unknown[];
    trash: unknown[];
  };
  /** localStorage 业务数据：进货明细/出库单/盘点/零售交易/挂单/日结/会员/退货/操作员/热销/小票模板/打印模板/待办/运营成本 等 */
  businessData: Record<string, unknown>;
  /** 业务设置（随备份一起导出；列宽/列显隐等 UI 偏好不导出） */
  settings: Record<string, unknown>;
}

// 随备份一起导出的业务数据（localStorage key 白名单，均为跨设备希望保留的业务数据，不含 UI 偏好）
// ⚠️ 多设备同步引擎（syncEngine.ts）复用此白名单 —— 增删业务 key 时备份与同步两处自动保持一致
export const BUSINESS_DATA_KEYS = [
  // 进货 / 库存
  'changfa_purchase_inbounds',     // 入库单 / 应付款
  'changfa_purchase_details',      // 进货明细（入库单）
  'changfa_inventory_outbounds',   // 出库单
  'changfa_stock_count_records',   // 盘点记录
  // 零售收银
  'cf_retail_transactions',        // 零售交易
  'cf_retail_pending_orders',      // 零售挂单
  'cf_retail_shifts',              // 零售日结 / 交接班
  'cf_retail_customers',           // 零售会员
  'cf_retail_returns',             // 零售退货
  'cf_retail_operators',           // 零售操作员
  'cf_retail_hot_products',        // 零售热销商品
  'cf_receipt_template',           // 小票模板
  // 业务配置 / 记录
  'changfa_print_templates',       // 送货/进货打印模板
  'changfa_todos_v1',              // 工作台待办事项
  'changfa_operating_costs_v3',    // 利润分析运营成本（工资/固定费用/加油/其他支出）
  'changfa_settled_payables_v1',   // 应付已结金额覆盖表
  'changfa_prepayments_v1',        // #577 预收预付余额流水记录
  'changfa_purchase_orders',       // 采购单（PO 状态机：draft→confirmed→arrived→completed）
  'changfa_purchase_returns',      // #749 供应商退货单（原因/方式/退款状态/扣库存留痕）
  'changfa_quotation_drafts',      // 已保存报价单（可在报价页继续编辑）
  'changfa_custom_festivals',      // 自定义节日定义
  'changfa_festival_retail_notes', // #568 节日零售手记本（无条码人工录入）
  'changfa_festival_notes',        // 节日小纸条（跨年注意事项/教训备忘）
  'changfa_exchange_records',      // #767 销售换货记录
  'changfa_returns',               // #767 销售退货记录
  'changfa_driver_locations',      // #789 司机位置上报（GPS 记录型数组，多司机按记录合并）
];

// 业务设置（localStorage key 白名单；列宽/列显隐等 UI 偏好不导出）
// ⚠️ 同 BUSINESS_DATA_KEYS：多设备同步引擎复用此白名单
export const SETTINGS_KEYS = [
  'changfa_favorite_addresses',    // 地图收藏地址
  'changfa_map_marker_preferences', // 地图图标个人显示偏好（按账号记录）
  'changfa_store_location',        // 老板校正的共享店铺位置
  'changfa_automation_state',      // 自动化任务状态
  'changfa_label_sizes',           // 标签打印自定义尺寸
  'changfa_label_field_order',     // 标签打印字段顺序
  'changfa_label_style',           // 标签打印样式配置
  'changfa_label_remark',          // 标签备注行文字（纯文本，经 __raw 包装导出）
  'changfa_festival_window_days',           // 节日备货窗口天数（按节日覆盖）
  'changfa_festival_window_days_global',    // 节日备货窗口天数（全局，纯数字文本）
  'changfa_grid_print_templates',           // #767 网格打印模板
  'changfa_print_tpl_mydefault_v1',         // #767 打印模板「我的默认值」快照
  'changfa_permission_matrix',              // #767 权限矩阵
  'changfa_cloud_print_settings',           // #767 云打印（映美）设置
  'changfa_share_templates',               // #798 分享模板（送货/报价/采购单多套配置）
];

const LAST_BACKUP_KEY = 'changfa_last_backup_at';
const HISTORY_KEY = 'changfa_backup_history';
const AUTO_BACKUP_KEY = 'changfa_auto_backup'; // 'on' | 'off'，默认 on（自动提醒）

export const REMIND_INTERVAL_MS = 24 * 60 * 60 * 1000; // 超过 24h 未备份则提醒

// ===== 收集 / 导出 =====

/** __raw 包装标记：部分键存的是纯文本（非 JSON，如标签备注行），打包时包成 { __raw: 原文 }，恢复时解包写回 */
const RAW_WRAPPER_KEY = '__raw';

/** 判断备份值是否为 __raw 包装的纯文本 */
function isRawWrapped(v: unknown): v is { __raw: string } {
  return (
    typeof v === 'object' && v !== null && !Array.isArray(v) &&
    Object.keys(v as Record<string, unknown>).length === 1 &&
    typeof (v as Record<string, unknown>)[RAW_WRAPPER_KEY] === 'string'
  );
}

function readLocalStorageList(keys: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    try {
      const v = localStorage.getItem(k);
      if (v == null) continue;
      try {
        out[k] = JSON.parse(v);
      } catch {
        // 非 JSON 纯文本键（如 changfa_label_remark）——__raw 包装后导出
        out[k] = { [RAW_WRAPPER_KEY]: v };
      }
    } catch { /* Best-effort operation; failure is non-fatal. */ }
  }
  return out;
}

/** 把备份中的 localStorage 段写回（__raw 包装的键还原为原文，其余 JSON.stringify） */
function writeBackLocalStorage(section: Record<string, unknown> | undefined): void {
  for (const [k, v] of Object.entries(section ?? {})) {
    try {
      if (isRawWrapped(v)) {
        localStorage.setItem(k, v.__raw);
      } else {
        localStorage.setItem(k, JSON.stringify(v));
      }
    } catch { /* Best-effort operation; failure is non-fatal. */ }
  }
}

export async function collectBackup(): Promise<BackupFile> {
  const [customers, suppliers, products, salesOrders, warehouses, trash] = await Promise.all([
    dbGet<unknown[]>(KEYS.customers),
    dbGet<unknown[]>(KEYS.suppliers),
    dbGet<unknown[]>(KEYS.products),
    dbGet<unknown[]>(KEYS.salesOrders),
    dbGet<unknown[]>(KEYS.warehouses),
    dbGet<unknown[]>(KEYS.trash),
  ]);

  const businessData = readLocalStorageList(BUSINESS_DATA_KEYS);
  const settings = readLocalStorageList(SETTINGS_KEYS);

  return {
    app: 'changfa-workbench',
    type: 'changfa-backup',
    formatVersion: 2,
    dataVersion: DATA_VERSION,
    exportedAt: new Date().toISOString(),
    stats: {
      customers: customers?.length ?? 0,
      suppliers: suppliers?.length ?? 0,
      products: products?.length ?? 0,
      salesOrders: salesOrders?.length ?? 0,
      warehouses: warehouses?.length ?? 0,
      trash: trash?.length ?? 0,
      businessData: Object.keys(businessData).length,
      settings: Object.keys(settings).length,
    },
    data: {
      customers: customers ?? [],
      suppliers: suppliers ?? [],
      products: products ?? [],
      salesOrders: salesOrders ?? [],
      warehouses: warehouses ?? [],
      trash: trash ?? [],
    },
    businessData,
    settings,
  };
}

export function backupFileName(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `changfa-backup-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.json`;
}

/** 导出备份包为本地文件下载 */
export async function downloadBackup(): Promise<string> {
  const backup = await collectBackup();
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = backupFileName();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 3000);
  markBackupDone();
  return a.download;
}

// ===== 解析 / 导入 =====
export function parseBackup(text: string): BackupFile {
  let obj: unknown;
  try {
    obj = JSON.parse(text);
  } catch {
    throw new Error('文件不是有效的 JSON，请确认选择的是备份文件');
  }
  const b = obj as Partial<BackupFile>;
  if (b?.app !== 'changfa-workbench' || b?.type !== 'changfa-backup' || !b?.data) {
    throw new Error('不是昌发工作台备份文件（缺少备份标识）');
  }
  if (!Array.isArray(b.data.customers) || !Array.isArray(b.data.suppliers) ||
      !Array.isArray(b.data.products) || !Array.isArray(b.data.salesOrders)) {
    throw new Error('备份文件数据不完整（4 类核心业务数据缺失）');
  }
  // v1 旧备份：补齐 v2 字段（warehouses/trash/businessData 为空）
  const normalized: BackupFile = {
    ...(b as BackupFile),
    formatVersion: 2,
    data: {
      customers: b.data.customers,
      suppliers: b.data.suppliers,
      products: b.data.products,
      salesOrders: b.data.salesOrders,
      warehouses: Array.isArray(b.data.warehouses) ? b.data.warehouses : [],
      trash: Array.isArray(b.data.trash) ? b.data.trash : [],
    },
    businessData: b.businessData && typeof b.businessData === 'object' ? b.businessData : {},
    settings: b.settings && typeof b.settings === 'object' ? b.settings : {},
  };
  return normalized;
}

export interface ImportResult {
  stats: BackupStats;
  dataVersion: string;
  exportedAt: string;
}

/** 从备份对象恢复全部数据（写入 IndexedDB + 内存 + 业务数据 + 设置）。加密启用时自动加密写入 */
export async function importBackup(backup: BackupFile): Promise<ImportResult> {
  await Promise.all([
    dbSet(KEYS.customers, backup.data.customers),
    dbSet(KEYS.suppliers, backup.data.suppliers),
    dbSet(KEYS.products, backup.data.products),
    dbSet(KEYS.salesOrders, backup.data.salesOrders),
    dbSet(KEYS.warehouses, backup.data.warehouses ?? []),
    dbSet(KEYS.trash, backup.data.trash ?? []),
    dbSet(KEYS.importedAt, new Date().toISOString()),
    dbSet(KEYS.version, backup.dataVersion),
  ]);

  // 写回 localStorage 业务数据（进货/库存/零售/待办/运营成本/打印模板/采购单/节日等）
  writeBackLocalStorage(backup.businessData);

  // 写回业务设置（标签打印配置/节日窗口天数等；__raw 纯文本键自动解包）
  writeBackLocalStorage(backup.settings);

  // 同步内存，页面无需刷新即可看到恢复后的数据
  setMockCustomers(backup.data.customers as never[]);
  setMockSuppliers(backup.data.suppliers as never[]);
  setMockProducts(backup.data.products as never[]);
  setMockSales(backup.data.salesOrders as never[]);

  return {
    stats: backup.stats,
    dataVersion: backup.dataVersion,
    exportedAt: backup.exportedAt,
  };
}

/** 从 File 对象导入（供文件选择器使用） */
export async function importBackupFromFile(file: File): Promise<ImportResult> {
  const text = await file.text();
  const backup = parseBackup(text);
  return importBackup(backup);
}

// ===== 备份记录与自动提醒 =====
export interface BackupRecord {
  at: string;
  name: string;
  mode: 'local' | 'aliyunpan' | 'webdav' | 'supabase' | 'qiniu';
}

export function markBackupDone(mode: 'local' | 'aliyunpan' | 'webdav' | 'supabase' | 'qiniu' = 'local', name = ''): void {
  try {
    localStorage.setItem(LAST_BACKUP_KEY, String(Date.now()));
    const rec: BackupRecord = { at: new Date().toISOString(), name, mode };
    const list = getBackupHistory();
    list.unshift(rec);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 50)));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export function lastBackupAt(): number {
  try {
    return Number(localStorage.getItem(LAST_BACKUP_KEY) || 0);
  } catch {
    return 0;
  }
}

export function getBackupHistory(): BackupRecord[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as BackupRecord[]) : [];
  } catch {
    return [];
  }
}

/** 距上次备份是否超过提醒间隔（24h） */
export function shouldRemindBackup(): boolean {
  const last = lastBackupAt();
  if (!last) return true; // 从未备份过
  return Date.now() - last > REMIND_INTERVAL_MS;
}

export function isAutoBackupOn(): boolean {
  try {
    return localStorage.getItem(AUTO_BACKUP_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setAutoBackup(on: boolean): void {
  try {
    localStorage.setItem(AUTO_BACKUP_KEY, on ? 'on' : 'off');
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

// ===== 飞牛 NAS（WebDAV）通道：支持多台 NAS =====
const WEBDAV_CFG_KEY = 'changfa_webdav_cfg'; // 旧版单实例键（自动迁移）
const WEBDAV_CFG_KEY_V2 = 'changfa_webdav_cfg_v2'; // 多实例列表
const WEBDAV_PENDING_KEY = 'changfa_webdav_pending'; // 仅存标志位，备份内容现场生成，避免大 JSON 塞 localStorage
const WEBDAV_LAST_SYNC_KEY = 'changfa_last_webdav_sync_at';

/** 打开工作台时距上次飞牛同步超过该间隔才再次同步（NAS 不常开，避免每次打开都重复上传） */
export const WEBDAV_SYNC_INTERVAL_MS = 6 * 60 * 60 * 1000;

/** 自动同步使用固定文件名（覆盖式上传）：StrictMode 双挂载重复执行不会累积文件 */
export const WEBDAV_LATEST_NAME = '昌发工作台最新备份.json';

/** 读取全部飞牛配置；若存在旧版单实例键则自动迁移到列表（url 未重复时并入） */
export function getAllWebDavConfigs(): WebDavConfig[] {
  try {
    let list: WebDavConfig[] = [];
    const v2Raw = localStorage.getItem(WEBDAV_CFG_KEY_V2);
    if (v2Raw) {
      try {
        const parsed = JSON.parse(v2Raw);
        if (Array.isArray(parsed)) list = parsed;
      } catch { /* Best-effort operation; failure is non-fatal. */ }
    }
    const legacyRaw = localStorage.getItem(WEBDAV_CFG_KEY);
    if (legacyRaw) {
      try {
        const legacy = JSON.parse(legacyRaw) as WebDavConfig;
        if (legacy && legacy.url && !list.some((c) => c?.url === legacy.url)) {
          list.push(legacy);
        }
        // 迁移完成：写入 v2 列表并清旧键
        localStorage.setItem(WEBDAV_CFG_KEY_V2, JSON.stringify(list));
        localStorage.removeItem(WEBDAV_CFG_KEY);
      } catch { /* Best-effort operation; failure is non-fatal. */ }
    }
    return list.filter((c) => c && c.url);
  } catch {
    return [];
  }
}

export function saveWebDavConfigs(cfgs: WebDavConfig[]): void {
  try {
    localStorage.setItem(WEBDAV_CFG_KEY_V2, JSON.stringify(cfgs.filter((c) => c && c.url)));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

/** 新增一台飞牛（url 重复时忽略），返回最新列表 */
export function addWebDavConfig(cfg: WebDavConfig): WebDavConfig[] {
  const list = getAllWebDavConfigs();
  if (cfg?.url && !list.some((c) => c.url === cfg.url)) {
    list.push(cfg);
    saveWebDavConfigs(list);
  }
  return list;
}

/** 按 url 移除一台飞牛，返回最新列表 */
export function removeWebDavConfig(url: string): WebDavConfig[] {
  const list = getAllWebDavConfigs().filter((c) => c.url !== url);
  saveWebDavConfigs(list);
  if (list.length === 0) {
    try {
      localStorage.removeItem(WEBDAV_PENDING_KEY);
      localStorage.removeItem(WEBDAV_LAST_SYNC_KEY);
    } catch { /* Best-effort operation; failure is non-fatal. */ }
  }
  return list;
}

/** 兼容旧调用：返回第一台飞牛配置（无则 null） */
export function getWebDavConfig(): WebDavConfig | null {
  return getAllWebDavConfigs()[0] ?? null;
}

/** 兼容旧调用：按 url 覆盖或追加到列表 */
export function saveWebDavConfig(cfg: WebDavConfig): void {
  const list = getAllWebDavConfigs();
  const idx = list.findIndex((c) => c.url === cfg.url);
  if (idx >= 0) list[idx] = cfg;
  else list.push(cfg);
  saveWebDavConfigs(list);
}

/** 兼容旧调用：清空全部飞牛配置 */
export function clearWebDavConfig(): void {
  try {
    localStorage.removeItem(WEBDAV_CFG_KEY_V2);
    localStorage.removeItem(WEBDAV_CFG_KEY);
    localStorage.removeItem(WEBDAV_PENDING_KEY);
    localStorage.removeItem(WEBDAV_LAST_SYNC_KEY);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export function markWebDavPending(): void {
  try {
    localStorage.setItem(WEBDAV_PENDING_KEY, '1');
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export function clearWebDavPending(): void {
  try {
    localStorage.removeItem(WEBDAV_PENDING_KEY);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export function isWebDavPending(): boolean {
  try {
    return localStorage.getItem(WEBDAV_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function lastWebDavSyncAt(): number {
  try {
    return Number(localStorage.getItem(WEBDAV_LAST_SYNC_KEY) || 0);
  } catch {
    return 0;
  }
}

export interface WebDavPushResult {
  ok: boolean;
  name?: string;
  message: string;
  status?: number;
}

/**
 * 把数据上传到飞牛 NAS（成功则记录备份 + 更新时间戳 + 清待同步标志）。
 * 支持传入预打包内容（全渠道备份时复用同一份数据，避免重复打包）；
 * 未传则现场 collectBackup 打包。
 */
export async function pushToWebDav(
  cfg: WebDavConfig,
  name = backupFileName(),
  content?: string,
): Promise<WebDavPushResult> {
  let payload: string;
  try {
    payload = content ?? JSON.stringify(await collectBackup());
  } catch (e) {
    return { ok: false, message: `打包数据失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
  try {
    const res = await webDavUpload(cfg, name, payload);
    if (res.ok) {
      markBackupDone('webdav', name);
      try {
        localStorage.setItem(WEBDAV_LAST_SYNC_KEY, String(Date.now()));
      } catch { /* Best-effort operation; failure is non-fatal. */ }
      clearWebDavPending();
    }
    return { ...res, name };
  } catch (e) {
    return { ok: false, name, message: `上传失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
}

// ===== Supabase Storage 通道（浏览器端 REST 直传，零依赖） =====
const SUPA_CFG_KEY = 'changfa_supabase_cfg';

export interface SupabaseConfig {
  /** 项目 URL，如 https://xxxx.supabase.co */
  url: string;
  /** anon public key（客户端安全） */
  anonKey: string;
  /** 存储桶名称 */
  bucket: string;
}

export function getSupabaseConfig(): SupabaseConfig | null {
  try {
    const raw = localStorage.getItem(SUPA_CFG_KEY);
    return raw ? (JSON.parse(raw) as SupabaseConfig) : null;
  } catch {
    return null;
  }
}

export function saveSupabaseConfig(cfg: SupabaseConfig): void {
  try {
    localStorage.setItem(SUPA_CFG_KEY, JSON.stringify(cfg));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export function clearSupabaseConfig(): void {
  try {
    localStorage.removeItem(SUPA_CFG_KEY);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export interface SupabasePushResult {
  ok: boolean;
  name?: string;
  message: string;
  status?: number;
}

/** 上传一个 JSON 对象到 Supabase Storage；返回 HTTP 状态码以便排查 */
async function supabaseUploadObject(
  cfg: SupabaseConfig,
  path: string,
  body: string,
): Promise<{ ok: boolean; status: number; message: string }> {
  const url = cfg.url.replace(/\/+$/, '');
  const res = await fetch(`${url}/storage/v1/object/${encodeURIComponent(cfg.bucket)}/${encodeURIComponent(path)}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${cfg.anonKey}`,
      apikey: cfg.anonKey,
      'Content-Type': 'application/json',
      'x-upsert': 'true',
    },
    body,
  });
  if (res.ok) {
    return { ok: true, status: res.status, message: '上传成功' };
  }
  let detail = '';
  try {
    const j = (await res.json()) as { message?: string; error?: string };
    detail = j?.message || j?.error || '';
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return { ok: false, status: res.status, message: detail || `HTTP ${res.status} ${res.statusText}`.trim() };
}

/** 测试 Supabase 连接：上传一个小探针文件验证 URL / anon key / bucket / 写入权限 */
export async function testSupabaseConnection(cfg: SupabaseConfig): Promise<SupabasePushResult> {
  if (!cfg.url || !cfg.anonKey || !cfg.bucket) {
    return { ok: false, message: '请先填写 Supabase 项目 URL、anon key 和 bucket 名称' };
  }
  try {
    // Supabase Storage 覆盖已存在对象走 UPDATE 路径会被 RLS 拦截，
    // 因此先删除旧探针（不存在时忽略），再以全新 INSERT 上传
    const baseUrl = cfg.url.replace(/\/+$/, '');
    await fetch(
      `${baseUrl}/storage/v1/object/${encodeURIComponent(cfg.bucket)}/${encodeURIComponent('__changfa_probe__.json')}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${cfg.anonKey}`,
          apikey: cfg.anonKey,
        },
      },
    ).catch(() => null);
    const res = await supabaseUploadObject(
      cfg,
      '__changfa_probe__.json',
      JSON.stringify({ probe: true, t: Date.now() }),
    );
    return res.ok
      ? { ok: true, message: '连接成功，写入权限正常' }
      : { ok: false, message: `连接失败：${res.message}`, status: res.status };
  } catch (e) {
    return { ok: false, message: `连接失败：${e instanceof Error ? e.message : '网络错误'}` };
  }
}

/** 上传备份包到 Supabase Storage（成功则记录备份） */
export async function pushToSupabase(
  cfg: SupabaseConfig,
  backup: BackupFile,
  name = backupFileName(),
): Promise<SupabasePushResult> {
  if (!cfg.url || !cfg.anonKey || !cfg.bucket) {
    return { ok: false, message: 'Supabase 配置不完整，请先绑定' };
  }
  try {
    const res = await supabaseUploadObject(cfg, name, JSON.stringify(backup));
    if (res.ok) {
      markBackupDone('supabase', name);
      return { ok: true, name, message: '已上传到 Supabase' };
    }
    return { ok: false, name, message: `上传失败：${res.message}`, status: res.status };
  } catch (e) {
    return { ok: false, name, message: `上传失败：${e instanceof Error ? e.message : '网络错误'}` };
  }
}

// ===== 本地备份记录（全渠道备份用，不触发浏览器下载） =====
/** 记录一次本地备份（写入备份时间 + 历史记录），返回备份文件名 */
export async function saveLocalBackup(backup?: BackupFile): Promise<string> {
  if (!backup) await collectBackup();
  const name = backupFileName();
  markBackupDone('local', name);
  return name;
}

// ===== 七牛云 Kodo 通道（Edge Function 签名 + 浏览器直传） =====
const QINIU_CFG_KEY = 'changfa_qiniu_cfg';
const QINIU_LAST_SYNC_KEY = 'changfa_last_qiniu_sync_at';

/** 自动同步间隔（与飞牛一致，6 小时；Edge Function 在线即补推） */
export const QINIU_SYNC_INTERVAL_MS = 6 * 60 * 60 * 1000;

/** 自动同步使用固定文件名（覆盖式上传，幂等） */
export const QINIU_LATEST_NAME = '昌发工作台最新备份.json';

export type { QiniuConfig, QiniuFile };

export function getQiniuConfig(): QiniuConfig | null {
  try {
    const raw = localStorage.getItem(QINIU_CFG_KEY);
    return raw ? (JSON.parse(raw) as QiniuConfig) : null;
  } catch {
    return null;
  }
}

export function saveQiniuConfig(cfg: QiniuConfig): void {
  try {
    localStorage.setItem(QINIU_CFG_KEY, JSON.stringify(cfg));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export function clearQiniuConfig(): void {
  try {
    localStorage.removeItem(QINIU_CFG_KEY);
    localStorage.removeItem(QINIU_LAST_SYNC_KEY);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export function lastQiniuSyncAt(): number {
  try {
    return Number(localStorage.getItem(QINIU_LAST_SYNC_KEY) || 0);
  } catch {
    return 0;
  }
}

export interface QiniuPushResult {
  ok: boolean;
  name?: string;
  message: string;
  status?: number;
}

/**
 * 上传备份包到七牛 Kodo（成功则记录备份 + 更新时间戳）。
 * 支持传入预打包内容（全渠道备份时复用同一份数据）。
 */
export async function pushToQiniu(
  cfg: QiniuConfig,
  name = backupFileName(),
  content?: string,
): Promise<QiniuPushResult> {
  let payload: string;
  try {
    payload = content ?? JSON.stringify(await collectBackup());
  } catch (e) {
    return { ok: false, message: `打包数据失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
  try {
    const res = await qiniuUpload(cfg, name, payload);
    if (res.ok) {
      markBackupDone('qiniu', name);
      try {
        localStorage.setItem(QINIU_LAST_SYNC_KEY, String(Date.now()));
      } catch { /* Best-effort operation; failure is non-fatal. */ }
    }
    return { ...res, name };
  } catch (e) {
    return { ok: false, name, message: `上传失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
}

/** 列举七牛存储空间中的备份文件（供 SystemPage 文件列表使用） */
export async function listQiniuBackups(cfg: QiniuConfig): Promise<{ ok: boolean; files: QiniuFile[]; message?: string }> {
  try {
    const res = await qiniuList(cfg, '', 100, '');
    return { ok: res.ok, files: res.files, message: res.message };
  } catch (e) {
    return { ok: false, files: [], message: `列举失败：${e instanceof Error ? e.message : '未知错误'}` };
  }
}

/** 从七牛下载指定备份文件并恢复（返回 JSON 文本供 parseBackup） */
export async function downloadQiniuBackup(cfg: QiniuConfig, key: string): Promise<{ ok: boolean; text?: string; message?: string }> {
  return qiniuDownloadText(cfg, key);
}
